import { Route } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { LogoutExtComponent } from './b2c-policy-core/logout-ext/logout-ext.component';
import { LogoutComponent } from './b2c-policy-core/logout/logout.component';
import { EstimateSettingGuard } from './guards/estimate-setting-guard/estimate-setting.guard';
import { HomeGuard } from './guards/home-guard/home.guard';
import { MainGuard } from './guards/main-guard/main.guard';
import { OtpGuard } from './guards/otp-guard/otp.guard';
import { LayoutComponent } from './layout/layout.component';
import { RegisteredGarageAssignmentActionComponent } from './modules/claim/claim-management/third-party-registered-garages/registered-garage-assignment-action/registered-garage-assignment-action.component';
import { RegisteredGarageRequestEstimateActionComponent } from './modules/claim/claim-management/third-party-registered-garages/registered-garage-request-estimate-action/registered-garage-request-estimate-action.component';
import { RegisteredGarageRequestInvoiceActionComponent } from './modules/claim/claim-management/third-party-registered-garages/registered-garage-request-invoice-action/registered-garage-request-invoice-action.component';
import { VerifyOtpComponent } from './modules/claim/claim-management/third-party-registered-garages/verify-otp/verify-otp.component';
import { LandingPageComponent } from './modules/fnol/landing-page/landing-page.component';
import { SubmitMissingInfoV2Component } from './modules/quote-assessment/submit-missing-info-v2/submit-missing-info-v2.component';
import { EstimateApprovalComponent } from './modules/repair/estimate-approval/estimate-approval.component';
import { SupplierApprovalComponent } from './modules/repair/supplier-approval/supplier-approval.component';
import { CustomerFeedback } from './public/customer-feedback/customer-feedback.component';
import { NoAccessErrorComponent } from './shared/error-page/no-access-error/no-access-error.component';
import { NotFoundErrorComponent } from './shared/error-page/not-found-error/not-found-error.component';
import { FreeTrialSignupComponent } from './public/free-trial-signup/free-trial-signup.component';
import { AgentixTrialSignupComponent } from './public/agentix-trial-signup/agentix-trial-signup.component';
import { OnboardingComponent } from './public/onboarding/onboarding.component';
import { userDataResolver } from './resolvers/userData.resolver';
import { VersionControlResolver } from './resolvers/versionControl.resolver';
import { RefreshComponent } from './shared/refresh/refresh.component';
import { UnavailableComponent } from './shared/error-page/unavailable/unavailable.component';
import { FileClaimGuard } from './guards/file-claim-guard/file-claim.guard';
import { AiAgentExternalAssistantComponent } from './modules/ai-studio/ai-agent-external-assistant/ai-agent-external-assistant.component';
import { ExternalAssistantAccessDeniedComponent } from './modules/ai-studio/ai-agent-external-assistant/external-assistant-access-denied/external-assistant-access-denied.component';
import { EXTERNAL_ASSISTANT_ACCESS_DENIED_SEGMENT, ExternalAssistantGuard } from './guards/external-assistant-guard/external-assistant.guard';
import { OtpVerificationComponent } from './modules/claim/claim-management/third-party-registered-garages/otp-verification/otp-verification.component';
import { TelrReturnComponent } from './public/telr-return/telr-return.component';
import { TelrPaymentStatus } from './helper/telr-payment-return.util';

export const routes: Route[] = [
  //{ path: '', pathMatch: 'full', redirectTo: 'home' },
  { path: 'repair/estimate/:id/approval', pathMatch: 'full', component: EstimateApprovalComponent },
  {
    path: 'repair/:id/vehicle-inspection-preview',
    pathMatch: 'full',
    loadChildren: () => import('./modules/repair/vehicle-inspection/vehicle-inspection.module').then(m => m.VehicleInspectionModule)
  },
  { path: 'supplier/activate/:partnershipGUID/:orgId/:bodyshopName/approval', pathMatch: 'full', component: SupplierApprovalComponent },
  { path: 'case/estimate/:id/approval', pathMatch: 'full', component: EstimateApprovalComponent },
  { path: 'claim/assignment', canActivate: [ OtpGuard ], component: RegisteredGarageAssignmentActionComponent },
  { path: 'claim/estimate', canActivate: [ OtpGuard ], component: RegisteredGarageRequestEstimateActionComponent },
  { path: 'claim/invoice', canActivate: [ OtpGuard ], component: RegisteredGarageRequestInvoiceActionComponent },
  { path: 'claim/verify-otp', component: OtpVerificationComponent },
  { path: 'quote-assessment/verify-otp', component: VerifyOtpComponent, data: { module: 'quote-assessment' } },
  { path: 'logout', component: LogoutComponent },
  { path: 'logoutext', component: LogoutExtComponent },
  { path: 'landing/:name', component: LandingPageComponent },
  { path: 'landing', component: LandingPageComponent },
  { path: ':service/:objectId/feedback', component: CustomerFeedback },
  {
    path: 'quote-assessment/submit-missing-info',
    canActivate: [ OtpGuard ],
    component: SubmitMissingInfoV2Component,
    resolve: { version: VersionControlResolver }
  },
  { path: 'onboarding', component: OnboardingComponent },
  // No tenant code means no token can be issued, so show the access denied page directly
  { path: 'ai-agent-external-assistant', component: ExternalAssistantAccessDeniedComponent },
  { path: 'ai-agent-external-assistant/:tenantCode', canActivate: [ ExternalAssistantGuard ], component: AiAgentExternalAssistantComponent },
  // Kept under the ai-agent-external-assistant prefix so MSAL and init-config treat it as public
  {
    path: `ai-agent-external-assistant/:tenantCode/${EXTERNAL_ASSISTANT_ACCESS_DENIED_SEGMENT}`,
    component: ExternalAssistantAccessDeniedComponent
  },
  {
    path: 'file-claim/:source',
    canActivate: [ FileClaimGuard ],
    loadChildren: () => import('./modules/file-claim/file-claim.module').then(m => m.FileClaimModule)
  },
  {
    path: 'error/:code',
    component: UnavailableComponent
  },
  { path: 'trial-account', component: AgentixTrialSignupComponent },
  { path: 'agentix-trial-signup', component: FreeTrialSignupComponent },
  {
    path: 'telr/authorised',
    component: TelrReturnComponent,
    data: { status: TelrPaymentStatus.Authorised }
  },
  {
    path: 'telr/declined',
    component: TelrReturnComponent,
    data: { status: TelrPaymentStatus.Declined }
  },
  {
    path: 'telr/cancelled',
    component: TelrReturnComponent,
    data: { status: TelrPaymentStatus.Cancelled }
  },
  {
    path: '',
    canActivate: [ MsalGuard, MainGuard ],
    component: LayoutComponent,
    resolve: { userData: userDataResolver },
    data: { automotiveService: [ 'Addenda Auto' ] },
    children: [
      {
        path: 'home',
        //canActivate: [ MainGuard ],
        canActivate: [ HomeGuard ],
        loadChildren: () => import('./modules/home/home.module').then(m => m.HomeModule)
      },
      {
        path: 'inventory',
        data: { automotiveService: 'Addenda Inventory' },
        loadChildren: () => import('./modules/inventory/inventory.module').then(m => m.InventoryModule)
      },
      {
        path: 'dashboard/accounting',
        pathMatch: 'full',
        redirectTo: 'accounting/overview/dashboard'
      },
      {
        path: 'accounting',
        canActivate: [ MainGuard ],
        data: { automotiveService: [ 'Accounting', 'Addenda Auto' ] },
        loadChildren: () => import('./modules/accounting/accounting.module').then(m => m.AccountingModule)
      },
      {
        path: 'quote',
        //canActivate: [ MainGuard ],
        data: { automotiveService: 'Carheal Quote' },
        loadChildren: () => import('./modules/quote/quote.module').then(m => m.QuoteModule)
      },
      {
        path: 'repair',
        //canActivate: [ MainGuard ],
        data: { automotiveService: 'Addenda Repair' },
        loadChildren: () => import('./modules/repair/repair.module').then(m => m.RepairModule)
      },
      {
        path: 'settings',
        canActivate: [ EstimateSettingGuard ],
        data: { automotiveService: 'Addenda Repair' },
        loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule)
      },
      {
        path: 'profile',
        loadChildren: () => import('./modules/user-profile/user-profile.module').then(m => m.UserProfileModule)
      },
      {
        path: 'claim',
        //canActivate: [ MainGuard ],
        data: { automotiveService: [ 'Addenda Claims', 'Claim Surveyor' ] },
        loadChildren: () => import('./modules/claim/claim.module').then(m => m.ClaimsModule)
      },
      {
        path: 'claimSetting',
        //canActivate: [ MainGuard ],
        data: { automotiveService: 'Addenda Claims' },
        loadChildren: () => import('./modules/claim-settings/claim-settings.module').then(m => m.ClaimSettingsModule)
      },
      {
        path: 'repairer-view',
        // canActivate: [ MainGuard ],
        data: { automotiveService: 'Addenda Claims' },
        loadChildren: () => import('./modules/repairer-view/repairer-view.module').then(m => m.RepairerViewModule)
      },
      {
        path: 'case',
        //canActivate: [ MainGuard ],
        data: { automotiveService: 'Quote Assessment' },
        loadChildren: () => import('./modules/quote-assessment/quote-assessment.module').then(m => m.QuoteAssessmentModule)
      },
      {
        path: 'accessDenied',
        component: NoAccessErrorComponent
      },
      {
        path: 'notFound',
        component: NotFoundErrorComponent
      },
      {
        path: 'fnol',
        //canActivate: [ MainGuard, FnolGuard ],
        data: { automotiveService: 'Fnol' },
        loadChildren: () => import('./modules/fnol/fnol.module').then(m => m.FnolModule)
      },
      {
        path: 'customer-management',
        //canActivate: [ MainGuard ],
        data: { automotiveService: [ 'Addenda Repair', 'Addenda Auto' ] },
        loadChildren: () => import('./modules/crm/crm-routing.module').then(m => m.CrmRoutingModule)
      },
      {
        path: 'booking-management',
        //canActivate: [ MainGuard ],
        data: { automotiveService: 'Addenda Auto' },
        loadChildren: () => import('./modules/booking-management/booking.module').then(m => m.BookingModule)
      },
      {
        path: 'catalog-management',
        //canActivate: [ MainGuard ],
        data: { automotiveService: 'Addenda Auto' },
        loadChildren: () => import('./modules/catalog-management/catalog.module').then(m => m.CatalogModule)
      },
      {
        path: 'content-management',
        //canActivate: [ MainGuard ],
        data: { automotiveService: 'Addenda Auto' },
        loadChildren: () => import('./modules/content-management/content.module').then(m => m.ContentModule)
      },
      {
        path: 'employee-management',
        //canActivate: [ MainGuard ],
        data: { automotiveService: [ 'Addenda Repair', 'Addenda Auto', 'Addenda Health', 'Addenda ServeScape', 'Addenda Property' ] },
        loadChildren: () => import('./modules/employee-management/employee.module').then(m => m.EmployeeModule)
      },
      {
        path: 'hrm',
        //canActivate: [ MainGuard ],
        data: { automotiveService: [ 'HRMS' ] },
        loadChildren: () => import('./modules/employee-management/employee.module').then(m => m.EmployeeModule)
      },
      {
        path: 'payment-management',
        //canActivate: [ MainGuard ],
        data: { automotiveService: [ 'Addenda Auto' ] },
        loadChildren: () => import('./modules/payment-management/payment.module').then(m => m.PaymentModule)
      },
      {
        path: 'help-section',
        canActivate: [ HomeGuard ],
        loadChildren: () => import('./modules/repair/help-section/help-section.module').then(m => m.HelpSectionModule)
      },
      {
        path: 'auto',
        //canActivate: [ MainGuard ],
        data: { automotiveService: [ 'Addenda Auto' ] },
        loadChildren: () => import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'refresh',
        component: RefreshComponent
      },
      {
        path: 'ai-agent',
        //canActivate: [ MainGuard ],
        data: { automotiveService: [ 'Addenda Auto' ] },
        loadChildren: () => import('./modules/ai-agent/ai-agent.module').then(m => m.AIAgentModule)
      },
      {
        path: 'ai-studio',
        //canActivate: [ MainGuard ],
        data: { automotiveService: [ 'Addenda Auto', 'Addenda Health', 'Addenda ServeScape', 'Addenda Property' ] },
        loadChildren: () => import('./modules/ai-studio/ai-studio.module').then(m => m.AIStudioModule)
      }
    ]
  },
  { path: '', pathMatch: 'full', redirectTo: 'home' },
  {
    path: '**',
    redirectTo: 'home'
  }
];
